import Paper from "@mui/material/Paper";
import axios from "axios";
import moment from "moment-timezone";

import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useSignal, useSignalEffect } from "@preact/signals-react";
import React, { useState } from "react";
import { MAX_TABLE_ROWS } from "../data/constants";
import { app } from "../signals/AppSignal";

interface TypeInstallations {
  id: number;
  dateCreated: string;
  accountId: string;
  slug: string;
  accountName: string;
  userId: string;
  userName: string;
  userEmail: string;
  eventType: string;
  eventDate: string | null;
  appId: string;
  appName: string;
  appVersion: string;
}

const Installations = () => {
  useSignal(0);

  const columns: GridColDef[] = [
    {
      field: "id",
      type: "number",
      headerAlign: "right",
      headerName: "#",
      width: 50,
    },
    {
      field: "dateCreated",
      type: "string",
      headerAlign: "left",
      headerName: "Date Added",
      sortable: true,
      width: 200,
    },
    {
      field: "eventType",
      type: "string",
      headerAlign: "left",
      headerName: "Event",
      sortable: true,
      width: 100,
    },
    {
      field: "accountId",
      type: "string",
      headerAlign: "left",
      headerName: "Account Id",
      sortable: true,
      width: 120,
    },
    {
      field: "slug",
      type: "string",
      headerAlign: "left",
      headerName: "Slug",
      sortable: true,
      width: 150,
    },
    {
      field: "accountName",
      type: "string",
      headerAlign: "left",
      headerName: "Account Name",
      sortable: true,
      width: 150,
    },
    {
      field: "userId",
      type: "string",
      headerAlign: "left",
      headerName: "User Id",
      sortable: true,
      width: 100,
    },
    {
      field: "userName",
      type: "string",
      headerAlign: "left",
      headerName: "User Name",
      sortable: true,
      width: 150,
    },
    {
      field: "userEmail",
      type: "string",
      headerAlign: "left",
      headerName: "Email",
      sortable: true,
      width: 200,
    },

    {
      field: "appId",
      type: "string",
      headerAlign: "left",
      headerName: "App Id",
      sortable: true,
      width: 100,
    },
    {
      field: "appName",
      type: "string",
      headerAlign: "left",
      headerName: "App Name",
      sortable: true,
      width: 200,
    },
    {
      field: "appVersion",
      type: "string",
      headerAlign: "left",
      headerName: "App Version",
      sortable: true,
      width: 100,
    },
    {
      field: "eventDate",
      type: "string",
      headerAlign: "left",
      headerName: "Timestamp",
      sortable: true,
      width: 200,
    },
  ];

  const [installations, setInstallations] = useState<TypeInstallations[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pagnition, setPagnation] = useState<any>({});

  const [paginationModel, setPaginationModel] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 0, pageSize: 50 });

  const rowCount = installations.length || 0;
  const pageCount = Math.ceil(rowCount / paginationModel.pageSize);
  const startIndex = paginationModel.page * paginationModel.pageSize + 1;
  const endIndex = Math.min(
    (paginationModel.page + 1) * paginationModel.pageSize,
    rowCount,
  );

  const getTablePage = (totalPages: number) => {
    const pages: React.ReactNode[] = [];
    const currentPage = pagnition.currentPage;

    // Controls how many pages to show before and after the current page
    const neighbors = 2;

    for (let i = 1; i <= totalPages; i++) {
      // Always show the first page, the last page, and pages immediately surrounding the current page
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - neighbors && i <= currentPage + neighbors)
      ) {
        const isCurrent = currentPage === i;
        pages.push(
          <li key={i} className="page-item">
            <button
              disabled={isCurrent}
              className={
                isCurrent
                  ? "page-link text-white fw-bold active"
                  : "page-link text-dark"
              }
              onClick={() => getInstallations(i)}
            >
              {i}
            </button>
          </li>,
        );
      }
      // Add Ellipsis if we are skipping chunks of pages
      else if (
        i === currentPage - neighbors - 1 ||
        i === currentPage + neighbors + 1
      ) {
        pages.push(
          <li key={`ellipsis-${i}`} className="page-item disabled">
            <span className="page-link text-muted bg-light border-0">...</span>
          </li>,
        );
      }
    }

    return pages;
  };

  const handlePageSizeChange = (event: { target: { value: string } }) => {
    setPaginationModel((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      page: 0, // Reset to first page if pageSize changes
    }));
  };

  const handleNext = () => {
    setPaginationModel((prev) => ({
      ...prev,
      page: Math.min(prev.page + 1, pageCount - 1),
    }));
  };

  const handlePrev = () => {
    setPaginationModel((prev) => ({
      ...prev,
      page: Math.max(prev.page - 1, 0),
    }));
  };

  const convertUTCToLocal = (utcDateStr: string) => {
    return moment
      .utc(utcDateStr) // Parse as UTC
      .tz(moment.tz.guess())
      .format("MMMM D, YYYY, HH:mm:ss");
  };

  const getInstallations = async (page: number = 1) => {
    axios
      .get(
        `/api/v1/activities/${app.value}/installations?limit=${MAX_TABLE_ROWS}&page=${page}`,
      )
      .then((response) => {
        // handle success
        console.log(response.data);
        setPagnation(response.data.pagination);
        setPaginationModel({
          page: response.data.pagination.currentPage,
          pageSize: response.data.pagination.pageSize,
        });

        let installationList = response.data.data as TypeInstallations[];
        installationList = installationList.map((list, index) => ({
          ...list,
          id: index + 1,
          dateCreated: convertUTCToLocal(list.dateCreated),
          eventDate: list.eventDate ? convertUTCToLocal(list.eventDate) : "",
        }));
        setInstallations(installationList);
      })
      .catch((error) => {
        // handle error
        console.log(error);
        setInstallations([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useSignalEffect(() => {
    getInstallations();
  });

  return (
    <div className="row mt-2">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="d-lg-flex">
              <div>
                <h5 className="mb-0">Installations</h5>
              </div>
              <div className="ms-auto my-auto mt-lg-0 mt-4">
                <div className="ms-auto my-auto">
                  <button
                    type="button"
                    className="btn btn-icon-only btn-rounded btn-secondary mb-0 ms-2 btn-sm d-flex align-items-center justify-content-center ms-auto"
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    title="Refresh"
                    onClick={() => getInstallations()}
                  >
                    <i className="material-symbols-rounded text-md">sync</i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="mb-4 row align-items-center compact-controls">
              <div className="col-auto d-flex align-items-center gap-2">
                <select
                  className="form-select form-select-sm select-list"
                  onChange={handlePageSizeChange}
                  value={paginationModel.pageSize}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="30">30</option>
                  <option value="40">40</option>
                  <option value="50">50</option>
                </select>
                <span>entries per page</span>
              </div>

              <div className="col d-flex align-items-center gap-2">
                <button
                  className="m-0 btn btn-sm btn-outline-info"
                  onClick={handlePrev}
                  disabled={paginationModel.page === 0}
                >
                  Previous
                </button>

                <button
                  className="m-0 btn btn-sm btn-outline-info"
                  onClick={handleNext}
                  disabled={paginationModel.page >= pageCount - 1}
                >
                  Next
                </button>

                <div
                  className="d-flex align-items-center"
                  style={{ height: "100%" }}
                >
                  <span style={{ lineHeight: 1 }}>
                    Page {startIndex}–{endIndex} of {rowCount}
                  </span>
                </div>
              </div>

              <div className="col d-flex align-items-center justify-content-end">
                <nav aria-label="Table navigation">
                  <ul className="pagination pagination-sm mb-0 align-items-center">
                    {/* Previous Button */}
                    <li className="page-item">
                      <button
                        className="page-link text-dark"
                        disabled={pagnition?.currentPage === 1}
                        onClick={() => {
                          const prev = pagnition?.currentPage - 1;
                          if (prev >= 1) getInstallations(prev);
                        }}
                      >
                        <span aria-hidden="true">&laquo;</span>
                      </button>
                    </li>

                    {/* Dynamic Page List */}
                    {getTablePage(pagnition?.totalPages || 0)}

                    {/* Next Button */}
                    <li className="page-item">
                      <button
                        className="page-link text-dark"
                        disabled={
                          pagnition?.currentPage === pagnition?.totalPages
                        }
                        onClick={() => {
                          const next = pagnition?.currentPage + 1;
                          if (next <= pagnition?.totalPages)
                            getInstallations(next);
                        }}
                      >
                        <span aria-hidden="true">&raquo;</span>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <Paper sx={{ height: "550px", width: "100%" }}>
              <DataGrid
                loading={loading}
                rows={installations}
                columns={columns}
                slots={{ toolbar: GridToolbar }}
                initialState={{ pagination: { paginationModel } }}
                hideFooter={true}
                paginationModel={paginationModel}
                onPaginationModelChange={(newModel) =>
                  setPaginationModel(newModel)
                }
                pageSizeOptions={[10, 20, 30, 40, 50]}
                localeText={{ noRowsLabel: "No data available to display" }}
                disableRowSelectionOnClick
                sx={{ border: 0 }}
              />
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Installations;
